import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, CopyText, SvgIcon } from '@/components';
import { useAppSelector } from '@/redux/hooks';
import { amlTraceSelectors, profileSelectors } from '@/redux/selectors';
import { ChainsDictionary } from '@/config';
import { getIconByNetwork } from '@/helpers';
import useLocateTraceByUuid from '../TraceInvestigation/hooks/useLocateTraceByUuid';
import useTraceStatus from '../TraceInvestigation/hooks/useTraceStatus';
import {
  buildAiViewerUrl,
  getVisualizationHash,
} from '../TraceInvestigation/helpers';
import { TraceHistoryMessages } from '../TraceInvestigation/components/TraceHistoryMessages/TraceHistoryMessages';

import styles from './TraceInvestigationDetails.module.sass';

const VISIBLE_TX_COUNT = 2;

const VIEWER_LANGS = ['en', 'ua', 'ru'];

const TraceInvestigationDetails = (): JSX.Element => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  // Prefer the uuid carried in navigation state; fall back to ?uuid= so the
  // details page can be reopened straight from a shareable URL.
  const uuid =
    location?.state?.uuid ||
    new URLSearchParams(location?.search || '').get('uuid') ||
    undefined;

  const hasRealTopUp = useAppSelector(profileSelectors.hasRealTopUp);

  const [isTxListExpanded, setIsTxListExpanded] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [uuid]);

  const { item, notFound } = useLocateTraceByUuid(uuid);
  const traceUpdates = useAppSelector((state) =>
    amlTraceSelectors.getTraceById(state, uuid || ''),
  );
  const { visualizationUrl, statusMessages, errorMessages, hasVisualization } =
    useTraceStatus(item, traceUpdates);

  const hasErrors = errorMessages.length > 0;

  const txList = item?.txList || [];
  const hasHiddenTxs = txList.length > VISIBLE_TX_COUNT;
  const visibleTxList = isTxListExpanded
    ? txList
    : txList.slice(0, VISIBLE_TX_COUNT);

  const viewerLng = useMemo(() => {
    const lang = (i18n.language || '').slice(0, 2).toLowerCase();
    return VIEWER_LANGS.includes(lang) ? lang : 'en';
  }, [i18n.language]);

  const viewerUrl = useMemo(
    () =>
      visualizationUrl ? buildAiViewerUrl(visualizationUrl, viewerLng) : '',
    [visualizationUrl, viewerLng],
  );

  // Remount key: flip the frame when the first top-up arrives so the viewer
  // re-reads the case unmasked.
  const viewerHash = visualizationUrl
    ? getVisualizationHash(visualizationUrl)
    : '';

  return (
    <>
      {notFound && (
        <div className={styles.notFound}>
          {t('translation.ai_investigation_not_found')}
        </div>
      )}

      {!notFound && hasErrors && !hasVisualization && (
        <TraceHistoryMessages messages={errorMessages} />
      )}

      {!notFound && !hasErrors && !hasVisualization && (
        <TraceHistoryMessages messages={statusMessages} />
      )}

      {hasVisualization && visualizationUrl && (
        <>
          {visibleTxList.map((tx, index) => (
            <div
              key={`${tx.hash || tx.address}-${index}`}
              className={styles.summary}
            >
              <img
                src={getIconByNetwork(tx.blockchain)}
                alt={tx.blockchain}
                className={styles.summaryIcon}
              />
              <div className={styles.summaryInfo}>
                <CopyText
                  text={tx.hash || tx.address || ''}
                  copyText={tx.hash || tx.address || ''}
                  className={styles.summaryHash}
                />
                <div className={styles.summaryMeta}>
                  <span className={styles.summaryNetwork}>
                    {ChainsDictionary[tx.blockchain]?.name || tx.blockchain}
                  </span>
                  <span className={styles.summaryAsset}>{tx.asset}</span>
                </div>
              </div>
            </div>
          ))}

          {hasHiddenTxs && (
            <div className={styles.txListToggle}>
              <Button
                corner="rect"
                size="s"
                type="bordered"
                onClick={() => setIsTxListExpanded((prev) => !prev)}
              >
                {isTxListExpanded
                  ? t('translation.show_less')
                  : t('translation.show_more')}
              </Button>
            </div>
          )}

          {/* With no viewer origin configured, viewerUrl is '' and neither
              the frame nor the full-screen link is rendered (the tx summary
              list above still shows). */}
          {viewerUrl && (
            <div className={styles.visualization}>
              {hasRealTopUp && (
                <a
                  href={viewerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.fullscreenBtn}
                  aria-label="Full screen"
                >
                  <SvgIcon name="fullscreen" />
                </a>
              )}
              <iframe
                key={`${viewerHash}:${hasRealTopUp ? 'paid' : 'unpaid'}`}
                src={viewerUrl}
                title={`Trace visualization ${uuid}`}
                allow="clipboard-read; clipboard-write"
                loading="lazy"
                sandbox="allow-scripts allow-same-origin allow-popups allow-modals allow-forms allow-downloads allow-top-navigation-by-user-activation"
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

export default TraceInvestigationDetails;
